import {useState} from 'react';

import {Button, CheckBox, Input, Modal, Select} from '@/components';
import type {
  AdminBusinessAffiliateTier,
  CreateAffiliateTierPayload,
  UpdateAffiliateTierPayload,
} from '@/types';
import {BusinessAffiliateRewardModel} from '@/types';
import {capitalizeWords} from '@/utils/dashboard';

import ConfirmTierChangeModal from './ConfirmTierChangeModal';
import type {TierFieldErrors} from './types';

const REWARD_MODEL_OPTIONS = Object.values(BusinessAffiliateRewardModel).map(
  (model) => ({
    key: capitalizeWords(model.replace(/_/g, ' ')),
    value: model,
  }),
);

const emptyForm = (): CreateAffiliateTierPayload => ({
  name: '',
  rewardModel: BusinessAffiliateRewardModel.FeeShare,
  activationWindowDays: 30,
  rewardWindowDays: 60,
  termsVersion: 'v1',
  refereeDiscountPercent: 100,
  referrerSharePercent: 20,
  maxRewardPerReferralUSD: -1,
  maxRewardPerPayoutUSD: -1,
  requiresVerifiedReferrer: true,
  isDefault: false,
});

const toNumber = (value: string): number =>
  value === '' ? Number.NaN : Number(value);

type AffiliateTierFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isSaving: boolean;
  errors: TierFieldErrors;
  tier?: AdminBusinessAffiliateTier;
  currentDefault?: AdminBusinessAffiliateTier;
  validateCreate: (form: CreateAffiliateTierPayload) => boolean;
  validateUpdate: (form: UpdateAffiliateTierPayload) => boolean;
  onCreate: (form: CreateAffiliateTierPayload) => Promise<boolean>;
  onUpdate: (
    tierId: string,
    form: UpdateAffiliateTierPayload,
  ) => Promise<boolean>;
};

export default function AffiliateTierFormModal({
  isOpen,
  onClose,
  isSaving,
  errors,
  tier,
  currentDefault,
  validateCreate,
  validateUpdate,
  onCreate,
  onUpdate,
}: AffiliateTierFormModalProps) {
  const isEdit = Boolean(tier);
  const [form, setForm] = useState<CreateAffiliateTierPayload>(() =>
    tier
      ? {
          name: tier.name,
          rewardModel: tier.rewardModel,
          activationWindowDays: tier.activationWindowDays,
          rewardWindowDays: tier.rewardWindowDays,
          termsVersion: tier.termsVersion,
          refereeDiscountPercent: tier.refereeDiscountPercent,
          referrerSharePercent: tier.referrerSharePercent,
          volumeThresholdUSD: tier.volumeThresholdUSD,
          flatRewardUSD: tier.flatRewardUSD,
          volumeRewardBasisPoints: tier.volumeRewardBasisPoints,
          maxRewardPerReferralUSD: tier.maxRewardPerReferralUSD,
          maxRewardPerPayoutUSD: tier.maxRewardPerPayoutUSD,
          requiresVerifiedReferrer: tier.requiresVerifiedReferrer,
        }
      : emptyForm(),
  );
  const [isDeactivated, setIsDeactivated] = useState<boolean>(
    Boolean(tier?.isDeactivated),
  );

  const [isConfirming, setIsConfirming] = useState(false);

  const termsChanged = Boolean(
    tier && form.termsVersion.trim() !== tier.termsVersion,
  );
  const replacesDefault = Boolean(!tier && form.isDefault && currentDefault);
  const needsConfirm = termsChanged || replacesDefault;

  const setField = <K extends keyof CreateAffiliateTierPayload>(
    name: K,
    value: CreateAffiliateTierPayload[K],
  ) => setForm((prev) => ({...prev, [name]: value}));

  const handleModelChange = (rewardModel: BusinessAffiliateRewardModel) =>
    setForm((prev) => ({
      name: prev.name,
      activationWindowDays: prev.activationWindowDays,
      rewardWindowDays: prev.rewardWindowDays,
      termsVersion: prev.termsVersion,
      refereeDiscountPercent: prev.refereeDiscountPercent,
      maxRewardPerReferralUSD: prev.maxRewardPerReferralUSD,
      maxRewardPerPayoutUSD: prev.maxRewardPerPayoutUSD,
      requiresVerifiedReferrer: prev.requiresVerifiedReferrer,
      isDefault: prev.isDefault,
      rewardModel,
    }));

  const updatePayload = (): UpdateAffiliateTierPayload => ({
    name: form.name,
    isDeactivated,
    activationWindowDays: form.activationWindowDays,
    rewardWindowDays: form.rewardWindowDays,
    refereeDiscountPercent: form.refereeDiscountPercent,
    maxRewardPerReferralUSD: form.maxRewardPerReferralUSD,
    maxRewardPerPayoutUSD: form.maxRewardPerPayoutUSD,
    requiresVerifiedReferrer: form.requiresVerifiedReferrer,
    termsVersion: form.termsVersion,
  });

  const submit = async (): Promise<boolean> => {
    if (tier?.id) {
      return onUpdate(tier.id, updatePayload());
    }
    return onCreate(form);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (needsConfirm) {
      // Check the form first, so a field error is not hidden behind the confirm.
      const isValid = tier
        ? validateUpdate(updatePayload())
        : validateCreate(form);
      if (isValid) {
        setIsConfirming(true);
      }
      return;
    }
    await submit();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width="w-11/12 md:w-7/12 lg:w-5/12 px-4 sm:px-0"
        title={isEdit ? 'Edit affiliate tier' : 'Create affiliate tier'}
        closeOnOutsideClick
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            name="name"
            type="text"
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setField('name', e.target.value)
            }
            className="w-full isFull"
            placeholder="e.g. Launch fee share"
            error={errors.name}
            data-testid="affiliate-tier-name"
          />

          <Select
            label="Reward model"
            name="rewardModel"
            value={form.rewardModel}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleModelChange(e.target.value as BusinessAffiliateRewardModel)
            }
            options={REWARD_MODEL_OPTIONS}
            className="w-full"
            disabled={isEdit}
            error={errors.rewardModel}
            data-testid="affiliate-tier-reward-model"
          />
          {isEdit ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              The reward model and its amounts are fixed once a tier exists.
              Create a replacement tier and make it the default instead.
            </p>
          ) : null}

          <div className="flex flex-col md:flex-row gap-3">
            <Input
              label="Activation window (days)"
              name="activationWindowDays"
              type="number"
              value={form.activationWindowDays}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField('activationWindowDays', toNumber(e.target.value))
              }
              className="w-full isFull"
              error={errors.activationWindowDays}
            />
            <Input
              label="Reward window (days)"
              name="rewardWindowDays"
              type="number"
              value={form.rewardWindowDays}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField('rewardWindowDays', toNumber(e.target.value))
              }
              className="w-full isFull"
              error={errors.rewardWindowDays}
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Both windows run from the referee&apos;s sign-up, so a slow
            applicant gets a shorter earning period rather than a later one.
          </p>

          {form.rewardModel === BusinessAffiliateRewardModel.FeeShare ? (
            <Input
              label="Referrer share of the fee (%)"
              name="referrerSharePercent"
              type="number"
              value={form.referrerSharePercent ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField('referrerSharePercent', toNumber(e.target.value))
              }
              className="w-full isFull"
              disabled={isEdit}
              error={errors.referrerSharePercent}
              data-testid="affiliate-tier-referrer-share"
            />
          ) : null}

          {form.rewardModel === BusinessAffiliateRewardModel.FlatAtThreshold ? (
            <div className="flex flex-col md:flex-row gap-3">
              <Input
                label="Volume threshold (USD)"
                name="volumeThresholdUSD"
                type="number"
                value={form.volumeThresholdUSD ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setField('volumeThresholdUSD', toNumber(e.target.value))
                }
                className="w-full isFull"
                disabled={isEdit}
                error={errors.volumeThresholdUSD}
                data-testid="affiliate-tier-volume-threshold"
              />
              <Input
                label="Flat reward (USD)"
                name="flatRewardUSD"
                type="number"
                value={form.flatRewardUSD ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setField('flatRewardUSD', toNumber(e.target.value))
                }
                className="w-full isFull"
                disabled={isEdit}
                error={errors.flatRewardUSD}
                data-testid="affiliate-tier-flat-reward"
              />
            </div>
          ) : null}

          {form.rewardModel ===
          BusinessAffiliateRewardModel.VolumePercentage ? (
            <>
              <Input
                label="Reward (basis points of volume)"
                name="volumeRewardBasisPoints"
                type="number"
                value={form.volumeRewardBasisPoints ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setField('volumeRewardBasisPoints', toNumber(e.target.value))
                }
                className="w-full isFull"
                disabled={isEdit}
                error={errors.volumeRewardBasisPoints}
                data-testid="affiliate-tier-basis-points"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                100 bps = 1%.
              </p>
            </>
          ) : null}

          <Input
            label="Referee fee discount (%)"
            name="refereeDiscountPercent"
            type="number"
            value={form.refereeDiscountPercent ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setField('refereeDiscountPercent', toNumber(e.target.value))
            }
            className="w-full isFull"
            error={errors.refereeDiscountPercent}
          />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Applies to fees added to the amount sent. A fee taken out of the
            amount received is never waived, since that would pay the recipient
            more rather than cost the referee less.
          </p>

          <div className="flex flex-col md:flex-row gap-3">
            <Input
              label="Max reward per referral (USD)"
              name="maxRewardPerReferralUSD"
              type="number"
              value={form.maxRewardPerReferralUSD ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField('maxRewardPerReferralUSD', toNumber(e.target.value))
              }
              className="w-full isFull"
              error={errors.maxRewardPerReferralUSD}
            />
            <Input
              label="Max reward per payout (USD)"
              name="maxRewardPerPayoutUSD"
              type="number"
              value={form.maxRewardPerPayoutUSD ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField('maxRewardPerPayoutUSD', toNumber(e.target.value))
              }
              className="w-full isFull"
              error={errors.maxRewardPerPayoutUSD}
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Use -1 for no cap. A payout cap of 0 is not allowed.
          </p>

          <Input
            label="Terms version"
            name="termsVersion"
            type="text"
            value={form.termsVersion}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setField('termsVersion', e.target.value)
            }
            className="w-full isFull"
            error={errors.termsVersion}
            data-testid="affiliate-tier-terms-version"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Raising this suspends payouts to referrers until each accepts again.
            Rewards keep accruing in the meantime.
          </p>

          <CheckBox
            label="Only pay referrers whose KYB is verified"
            checked={Boolean(form.requiresVerifiedReferrer)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setField('requiresVerifiedReferrer', e.target.checked)
            }
          />

          {isEdit ? (
            <>
              <CheckBox
                label="Deactivated (no new referrals captured)"
                checked={isDeactivated}
                disabled={tier?.isDefault}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setIsDeactivated(e.target.checked)
                }
              />
              {tier?.isDefault ? (
                <p
                  className="text-sm text-gray-500 dark:text-gray-400"
                  data-testid="affiliate-tier-default-hint"
                >
                  This is the default tier, so it cannot be deactivated. Make
                  another tier the default first.
                </p>
              ) : null}
            </>
          ) : (
            <CheckBox
              label="Capture new referrals under this tier"
              checked={Boolean(form.isDefault)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField('isDefault', e.target.checked)
              }
            />
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" colorScheme="cyan" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="cyan"
              isLoading={isSaving}
              loadingText="Saving..."
              data-testid="affiliate-tier-submit"
            >
              {isEdit ? 'Save changes' : 'Create tier'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmTierChangeModal
        isOpen={isConfirming}
        title={
          termsChanged ? 'Change terms version' : 'Replace the default tier'
        }
        confirmLabel={
          termsChanged ? 'Change terms version' : 'Create and make default'
        }
        loadingText="Saving..."
        onClose={() => setIsConfirming(false)}
        onConfirm={submit}
        isDestructive={termsChanged}
      >
        {termsChanged ? (
          <p>
            Changing the terms version from{' '}
            <span className="font-mono">{tier?.termsVersion}</span> to{' '}
            <span className="font-mono">{form.termsVersion.trim()}</span>{' '}
            suspends payouts for every referrer on this programme until they
            accept again. Rewards keep accruing in the meantime.
            {tier?.isDefault
              ? ''
              : ' This tier is not the default, so no referrer is asked to accept until it becomes the default.'}
          </p>
        ) : (
          <p>
            New referrals will be captured under this tier instead of{' '}
            <span className="font-medium">{currentDefault?.name}</span>.
            {currentDefault &&
            currentDefault.termsVersion !== form.termsVersion.trim()
              ? ' Its terms version differs, so every referrer must accept the new terms before payouts resume.'
              : ''}
          </p>
        )}
      </ConfirmTierChangeModal>
    </>
  );
}
